import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";

type Row = {
  id: number;
  channel: "email" | "sms";
  audience: "customer" | "crew";
  recipient_name: string | null;
  recipient: string;
  subject: string | null;
  body: string;
  status: "sent" | "failed";
  error_message: string | null;
  created_at: string;
};

type Props = {
  projectId: string;
  refreshKey?: number;
};

function timeAgo(iso: string) {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.round(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  return `${d}d ago`;
}

export default function MessageHistory({ projectId, refreshKey = 0 }: Props) {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      const { data } = await supabase
        .from("project_messages")
        .select("*")
        .eq("project_id", projectId)
        .order("created_at", { ascending: false })
        .limit(8);
      if (!cancelled) {
        setRows((data as Row[]) || []);
        setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [projectId, refreshKey]);

  return (
    <div className="bg-white border border-ink-100 rounded-lg">
      <div className="px-5 pt-5 pb-3 border-b border-ink-100 flex items-center justify-between">
        <div>
          <h3 className="font-display text-base font-bold text-ink-900">Recent messages</h3>
          <p className="text-[11px] text-ink-500 mt-0.5">
            SMS and emails sent for this project
          </p>
        </div>
        {rows.length > 0 && (
          <span className="text-[10px] text-ink-400 tabular-nums">
            {rows.length} shown
          </span>
        )}
      </div>

      {loading ? (
        <div className="px-5 py-6 text-[12px] text-ink-400 text-center">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="px-5 py-8 text-center">
          <div className="w-10 h-10 mx-auto rounded-full bg-cream-100 flex items-center justify-center text-ink-400 mb-2">
            <i className="ri-chat-3-line"></i>
          </div>
          <div className="text-[12px] text-ink-500">
            No messages yet. Use Quick Actions or the contact buttons to reach out.
          </div>
        </div>
      ) : (
        <ul className="divide-y divide-ink-100">
          {rows.map((row) => {
            const open = expanded === row.id;
            return (
              <li key={row.id}>
                <button
                  onClick={() => setExpanded(open ? null : row.id)}
                  className="w-full px-5 py-3 text-left hover:bg-cream-50 transition-colors cursor-pointer"
                >
                  <div className="flex items-center gap-3">
                    <span
                      className={`w-8 h-8 rounded-md flex items-center justify-center shrink-0 ${
                        row.channel === "email"
                          ? "bg-indigo-50 text-indigo-600"
                          : "bg-emerald-50 text-emerald-600"
                      }`}
                    >
                      <i
                        className={
                          row.channel === "email"
                            ? "ri-mail-line"
                            : "ri-message-2-line"
                        }
                      ></i>
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-ink-900 truncate">
                          {row.subject || row.body.split("\n")[0].slice(0, 60)}
                        </span>
                        <span
                          className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full whitespace-nowrap ${
                            row.audience === "customer"
                              ? "bg-brand-50 text-brand-700"
                              : "bg-amber-50 text-amber-700"
                          }`}
                        >
                          {row.audience}
                        </span>
                      </div>
                      <div className="text-[11px] text-ink-500 truncate">
                        To {row.recipient_name || row.recipient} ·{" "}
                        <span className="font-mono">{row.recipient}</span>
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div
                        className={`text-[11px] font-bold ${
                          row.status === "sent" ? "text-emerald-600" : "text-rose-600"
                        }`}
                      >
                        {row.status === "sent" ? "delivered" : "failed"}
                      </div>
                      <div className="text-[10px] text-ink-400 tabular-nums">
                        {timeAgo(row.created_at)}
                      </div>
                    </div>
                    <i
                      className={`text-ink-300 ${
                        open ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"
                      }`}
                    ></i>
                  </div>

                  {open && (
                    <div className="mt-3 ml-11 pl-3 border-l-2 border-ink-100">
                      {row.subject && (
                        <div className="text-[11px] text-ink-500 mb-1">
                          <span className="font-semibold text-ink-700">Subject:</span>{" "}
                          {row.subject}
                        </div>
                      )}
                      <pre className="text-[12px] text-ink-700 whitespace-pre-wrap font-sans leading-relaxed">
                        {row.body}
                      </pre>
                      {row.error_message && (
                        <div className="mt-2 text-[11px] text-rose-700 bg-rose-50 rounded-md px-2 py-1.5">
                          <span className="font-semibold">Error:</span>{" "}
                          {row.error_message}
                        </div>
                      )}
                    </div>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}